/**
 * Honest gates for protocol quantification.
 * Scene numbers come from the loaded overlay; protocol numbers need pipeline files.
 */

import type { CaseManifest, Modality, ResultsSummary, StageId } from "@/ipc/contract";

export interface ProtocolGate {
  id: string;
  label: string;
  ready: boolean;
  value: string | null;
  missing: string;
  open?: { modality: Modality; id: StageId };
}

function nodesOf(manifest: CaseManifest) {
  return [
    ...manifest.images,
    ...manifest.masks,
    ...manifest.atlases,
    ...manifest.measurements,
    ...manifest.reports,
    ...manifest.transforms,
  ];
}

function hasRel(manifest: CaseManifest, needle: string): boolean {
  return nodesOf(manifest).some((n) => n.relPath.includes(needle));
}

function fmtMl(value: number | null | undefined): string | null {
  if (value == null || !Number.isFinite(value)) return null;
  return `${value.toFixed(2)} mL`;
}

export function protocolGates(manifest: CaseManifest | null, summary: ResultsSummary | null): ProtocolGate[] {
  if (!manifest) {
    return [
      {
        id: "case",
        label: "Open case",
        ready: false,
        value: null,
        missing: "Open or create a case before protocol numbers can exist.",
      },
    ];
  }

  const hasMniMask = hasRel(manifest, "stroke-mask_space-MNI152");
  const hasNativeMask = manifest.masks.some((n) => n.relPath.includes("stroke-mask"));
  const hasReport = Boolean(summary?.radiologicalReportText) || manifest.reports.length > 0 || hasRel(manifest, "automatic_radiological_report");
  const hasQfv = (summary?.qfvTables.length ?? 0) > 0 || hasRel(manifest, "_QFV.csv");
  const wantsPwi = manifest.pipelineKind === "pwi" || manifest.pipelineKind === "combined";

  return [
    {
      id: "core",
      label: "Stroke core (report)",
      ready: summary?.coreVolumeMl != null,
      value: fmtMl(summary?.coreVolumeMl),
      missing: hasMniMask || hasNativeMask
        ? "Reporting has not written a core volume yet."
        : "Needs a stroke mask from Lesion Seg, then Reporting.",
      open: hasMniMask || hasNativeMask
        ? { modality: "DWI", id: "report" }
        : { modality: "DWI", id: "inference" },
    },
    {
      id: "hp",
      label: "Hypoperfusion",
      ready: summary?.hypoperfusionVolumeMl != null,
      value: fmtMl(summary?.hypoperfusionVolumeMl),
      missing: wantsPwi
        ? "Needs the PWI hypoperfusion stage and its report."
        : "This case is DWI-only. Switch the protocol to DWI + PWI to compute HP.",
      open: wantsPwi ? { modality: "PWI", id: "inference" } : undefined,
    },
    {
      id: "mismatch",
      label: "Mismatch",
      ready: summary?.mismatchVolumeMl != null,
      value: fmtMl(summary?.mismatchVolumeMl),
      missing: "Needs both a DWI core volume and a PWI hypoperfusion volume.",
      open: wantsPwi ? { modality: "PWI", id: "report" } : { modality: "DWI", id: "report" },
    },
    {
      id: "aspects",
      label: "ASPECTS",
      ready: summary?.aspects != null,
      value: summary?.aspects != null ? String(summary.aspects) : null,
      missing: hasMniMask
        ? "ASPECTS is written by Reporting from the MNI mask."
        : "ASPECTS needs the lesion in MNI space (Registration + Lesion Seg), then Reporting.",
      open: hasMniMask ? { modality: "DWI", id: "report" } : { modality: "DWI", id: "registration" },
    },
    {
      id: "qfv",
      label: "QFV / atlas tables",
      ready: hasQfv,
      value: hasQfv
        ? summary?.qfvTables.length
          ? `${summary.qfvTables.length} atlas table(s)`
          : "On disk"
        : null,
      missing: "QFV CSVs are written by Reporting after an MNI lesion mask exists.",
      open: { modality: "DWI", id: "report" },
    },
    {
      id: "report",
      label: "Radiological report",
      ready: hasReport,
      value: hasReport ? "On disk" : null,
      missing: "The automatic report is written by the Reporting stage.",
      open: { modality: "DWI", id: "report" },
    },
  ];
}
